// The Maps JavaScript API, loaded once a page with the browser's key (BL-018, the maps design v3,
// package 4). The key is GOOGLE_MAPS_BROWSER_KEY, handed to the signed-in office by /auth/session
// (mapsKey): it is restricted to the company addresses and the Maps JavaScript and Places APIs, so it is
// meant to be in the page. Nothing here stores it. Without one there's nothing to load and GoogleMap
// draws its stand-in.
//
// One script per page, whatever number of maps: the first load starts it, the rest share it. Google can
// refuse the key after the script has loaded (a site missing from the key's list): it says so by calling
// window.gm_authFailure, and every map hears it (onRefused) and goes back to the stand-in. A script that
// can't be reached (offline, blocked) answers unreachable, and the next map tries again.
//
// Only the parts GoogleMap uses are typed here (MapsApi), so a test hands it a stand-in object and
// nothing calls Google.

/** A point on Google's map (google.maps.LatLng). */
export interface MapsLatLng {
  lat(): number;
  lng(): number;
}
export interface MapsPoint {
  x: number;
  y: number;
}
export interface MapsListener {
  remove(): void;
}
/** The map (google.maps.Map), as GoogleMap drives it. */
export interface MapsMap {
  setOptions(o: Record<string, unknown>): void;
  fitBounds(b: MapsLatLngBounds, padding?: number): void;
  setCenter(c: { lat: number; lng: number }): void;
  setZoom(z: number): void;
  getZoom(): number | undefined;
  addListener(event: string, fn: () => void): MapsListener;
}
export interface MapsLatLngBounds {
  extend(p: { lat: number; lng: number }): void;
}
export interface MapsProjection {
  fromLatLngToDivPixel(p: MapsLatLng): MapsPoint | null;
  fromContainerPixelToLatLng(p: MapsPoint): MapsLatLng | null;
}
/** google.maps.OverlayView, the base of our drawing on the map. */
export interface MapsOverlayView {
  onAdd(): void;
  draw(): void;
  onRemove(): void;
  getPanes(): { overlayMouseTarget: { append(el: unknown): void } } | null;
  getProjection(): MapsProjection | null;
  setMap(map: MapsMap | null): void;
}
export interface MapsApi {
  Map: new (el: unknown, options: Record<string, unknown>) => MapsMap;
  OverlayView: new () => MapsOverlayView;
  LatLng: new (lat: number, lng: number) => MapsLatLng;
  LatLngBounds: new () => MapsLatLngBounds;
  Point: new (x: number, y: number) => MapsPoint;
}

/** Why there's no map: Google refused the key, or the script couldn't be reached. */
export type MapsFailure = 'refused' | 'unreachable';
export type MapsLoad = { ok: true; api: MapsApi } | { ok: false; why: MapsFailure };

export interface MapsLoader {
  /** The API with this key; the first key a page loads is the one it keeps. */
  load(key: string): Promise<MapsLoad>;
  /** Whether Google has refused the key on this page. */
  refused(): boolean;
  /** Called once when Google refuses the key; the answer stops listening. */
  onRefused(fn: () => void): () => void;
}

const SCRIPT = 'https://maps.googleapis.com/maps/api/js';
const READY = '__flowMapsReady';
/** How long the script may take before it counts as unreachable. */
export const MAPS_LOAD_MS = 15_000;

/** The script's address: the weekly release, loaded async, calling READY when it's in. */
export const mapsScriptUrl = (key: string) =>
  `${SCRIPT}?key=${encodeURIComponent(key)}&v=weekly&loading=async&callback=${READY}`;

/** Where the loader works: the page's document and window (a stand-in in tests). */
export interface MapsWorld {
  document: {
    createElement(tag: 'script'): {
      src: string;
      async: boolean;
      onerror: ((e: unknown) => void) | null;
      remove(): void;
    };
    head: { append(el: unknown): void };
  };
  window: Record<string, unknown>;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(t: unknown): void;
}

interface GoogleNamespace extends Partial<MapsApi> {
  importLibrary?: (name: string) => Promise<Record<string, unknown>>;
}

/** The classes GoogleMap uses, from the libraries the async script loads on request. */
async function apiOf(g: GoogleNamespace): Promise<MapsApi | null> {
  if (g.importLibrary) {
    const [core, maps] = await Promise.all([g.importLibrary('core'), g.importLibrary('maps')]);
    const api = { ...g, ...core, ...maps } as Partial<MapsApi>;
    return api.Map && api.OverlayView && api.LatLng && api.LatLngBounds && api.Point
      ? (api as MapsApi)
      : null;
  }
  return g.Map && g.OverlayView && g.LatLng && g.LatLngBounds && g.Point ? (g as MapsApi) : null;
}

/** A loader for one page. */
export function mapsLoader(w: MapsWorld): MapsLoader {
  let pending: Promise<MapsLoad> | null = null;
  let isRefused = false;
  const refusedListeners = new Set<() => void>();
  const google = () => (w.window.google as { maps?: GoogleNamespace } | undefined)?.maps;

  // Google calls this when it refuses the key (a site missing from the key's list, an API not allowed)
  w.window.gm_authFailure = () => {
    if (isRefused) return;
    isRefused = true;
    for (const fn of [...refusedListeners]) fn();
    refusedListeners.clear();
  };

  const start = (key: string) =>
    new Promise<MapsLoad>((resolve) => {
      let done = false;
      const s = w.document.createElement('script');
      const finish = (answer: MapsLoad) => {
        if (done) return;
        done = true;
        w.clearTimeout(timer);
        w.window[READY] = undefined;
        if (!answer.ok) {
          s.remove();
          pending = null; // the next map tries again
        }
        resolve(answer);
      };
      const timer = w.setTimeout(() => finish({ ok: false, why: 'unreachable' }), MAPS_LOAD_MS);
      w.window[READY] = () => {
        const g = google();
        if (!g) return finish({ ok: false, why: 'unreachable' });
        apiOf(g).then(
          (api) => finish(api ? { ok: true, api } : { ok: false, why: 'unreachable' }),
          () => finish({ ok: false, why: 'unreachable' }),
        );
      };
      s.onerror = () => finish({ ok: false, why: 'unreachable' });
      s.async = true;
      s.src = mapsScriptUrl(key);
      w.document.head.append(s);
    });

  return {
    load(key) {
      if (isRefused) return Promise.resolve({ ok: false, why: 'refused' });
      if (pending) return pending;
      const g = google();
      // already on the page (another loader, or a reload of this module)
      if (g?.Map || g?.importLibrary)
        return (pending = apiOf(g).then((api) =>
          api ? { ok: true, api } : { ok: false, why: 'unreachable' },
        ));
      pending = start(key);
      return pending;
    },
    refused: () => isRefused,
    onRefused(fn) {
      if (isRefused) {
        fn();
        return () => undefined;
      }
      refusedListeners.add(fn);
      return () => {
        refusedListeners.delete(fn);
      };
    },
  };
}

let page: MapsLoader | null = null;
/** The page's own loader, made on first use; null where there's no page (a server render). */
export function pageMapsLoader(): MapsLoader | null {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null;
  page ??= mapsLoader({
    document: document as unknown as MapsWorld['document'],
    window: window as unknown as Record<string, unknown>,
    setTimeout: (fn, ms) => window.setTimeout(fn, ms),
    clearTimeout: (t) => window.clearTimeout(t as number),
  });
  return page;
}
